import React from 'react';

const About = () => {
  const principles = [
    {
      title: 'Built to last, not to impress',
      description: 'We believe enduring quality speaks louder than fleeting trends. Our buildings are designed for decades, not seasons.'
    },
    {
      title: 'One team, one vision',
      description: 'From the first sketch to the last detail, one team owns your project. No handoffs, no miscommunication, no compromises.'
    },
    {
      title: 'Precision is non-negotiable',
      description: 'The difference between good and extraordinary lives in the millimeters. Every joint, every detail, every material choice is calibrated for permanence.'
    }
  ];

  return (
    <div className="bg-[#f5f2ed] pt-24">
      {/* Hero */}
      <section className="section-padding">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-['Playfair_Display'] text-[#1a1a1a] mb-8 fade-in">
            The Practice
          </h1>
          <div className="space-y-6 text-lg md:text-xl text-[#1a1a1a] leading-relaxed">
            <p className="fade-in" style={{ animationDelay: '0.1s' }}>
              We started Hale Modular because we were tired of seeing beautiful designs compromised by mediocre execution. Tired of hearing clients say, "The architect drew it this way, but the contractor built it differently."
            </p>
            <p className="fade-in" style={{ animationDelay: '0.2s' }}>
              We believe buildings should outlast trends. That design and construction should never be separated. That a home or office built today should still feel solid, intentional, and beautiful thirty years from now.
            </p>
            <p className="fade-in" style={{ animationDelay: '0.3s' }}>
              This is not a service provider listing features. This is a design-build practice built on the belief that architecture is incomplete without craftsmanship — and that craftsmanship without design is just manual labor.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-padding bg-white">
        <div className="max-w-[1600px] mx-auto">
          <h2 className="text-4xl md:text-5xl font-['Playfair_Display'] text-[#1a1a1a] mb-16 text-center">
            Our Philosophy
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {principles.map((principle, index) => (
              <div
                key={index}
                className="fade-in"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <h3 className="text-2xl md:text-3xl font-['Playfair_Display'] text-[#1a1a1a] mb-4 leading-tight">
                  {principle.title}
                </h3>
                <p className="text-[#9c9590] leading-relaxed">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="section-padding">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="relative h-[500px] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/13023755/pexels-photo-13023755.jpeg"
                alt="Founder"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div>
              <h3 className="text-3xl font-['Playfair_Display'] text-[#1a1a1a] mb-4">
                Rohan Mehta, Founder
              </h3>
              <p className="text-[#9c9590] leading-relaxed mb-4">
                Before starting Hale Modular, I spent eight years watching great architectural ideas die during construction. Misaligned incentives. Poor communication. Contractors who saw drawings as suggestions, not commitments.
              </p>
              <p className="text-[#9c9590] leading-relaxed mb-4">
                Hale Modular exists to close that gap. We are architects, engineers, and project managers under one roof — accountable from concept to completion. Every project I oversee personally. Because your building carries my name.
              </p>
              <p className="text-[#9c9590] leading-relaxed">
                This is a hands-on practice, not a faceless firm. And every building we deliver is built to outlast us both.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="section-padding bg-[#1a1a1a] text-[#f5f2ed]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg md:text-xl text-[#9c9590] mb-6">
            Based in Mumbai & Gujarat. Working with architects and clients who value craft that endures.
          </p>
          <p className="text-base text-[#9c9590]">
            A focused team of architects, engineers, and project leads across two offices.
          </p>
        </div>
      </section>

      {/* Image Section */}
      <section className="section-padding">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="relative h-[400px] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1635284305301-e485d088bf94"
              alt="Architectural Detail"
              className="w-full h-full object-cover hover-scale"
              loading="lazy"
            />
          </div>
          <div className="relative h-[400px] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1503387837-b154d5074bd2"
              alt="Construction Precision"
              className="w-full h-full object-cover hover-scale"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
